import { Injectable } from '@angular/core';
import { AuthService, LogoutOptions } from '@auth0/auth0-angular';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  combineLatest,
  filter,
  firstValueFrom,
  from,
  interval,
  map,
  switchMap,
  tap
} from 'rxjs';
import jwt_decode from 'jwt-decode';
import { Logger } from '@sondermind/datadog-logging';
import { ConfigurationService } from '@sondermind/configuration';
import { SegmentSchema, SegmentService } from '@sondermind/segment';

enum AuthErrorCodes {
  MISSING_TRANSACTION = 'missing_transaction',
  ACCESS_DENIED = 'access_denied'
}

interface ITokenInfo {
  sub: string;
  meta: {
    authsvc_account_id: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class Auth0TokenService {
  auth0TokenBehaviorSubject$ = new BehaviorSubject<string>('');
  private auth0EnabledBehaviorSubject$ = new BehaviorSubject<boolean>(false);
  private isFirstLoginFromAuth0Signup = new BehaviorSubject<boolean>(false);

  heartbeatSubscription: Subscription | null = null;
  auth0Enabled$ = this.auth0EnabledBehaviorSubject$.asObservable();
  auth0Token$ = this.auth0TokenBehaviorSubject$.asObservable();
  authSuccess$ = new BehaviorSubject<boolean>(false);
  isFirstLoginFromAuth0Signup$ = this.isFirstLoginFromAuth0Signup.asObservable();

  constructor(
    private auth0: AuthService,
    private logger: Logger,
    private configService: ConfigurationService,
    private segmentService: SegmentService<SegmentSchema>
  ) {
    this.auth0.error$.subscribe((err: Error) => {
      if (err.message === AuthErrorCodes.ACCESS_DENIED) {
        this.logout();
      }
    });
  }

  get auth0Enabled(): boolean {
    return this.auth0EnabledBehaviorSubject$.getValue();
  }

  initializeAuth0(): Observable<string | null> {
    this.auth0EnabledBehaviorSubject$.next(true);

    return combineLatest([this.auth0.isLoading$, this.auth0.isAuthenticated$]).pipe(
      filter(([isLoading, isAuthenticated]) => !isLoading),
      map(([_, isAuthenticated]) => isAuthenticated),
      switchMap((isAuthenticated) => {
        if (!isAuthenticated) {
          // not logged in yet → kick off redirect, emit null
          return from(
            this.auth0.loginWithRedirect({
              authorizationParams: {
                scope: 'offline_access',
                eeNamespace: this.configService.env.eeNamespace
              }
            })
          ).pipe(map(() => null as string | null));
        }
        // user is authenticated → get token
        return from(
          firstValueFrom(
            this.auth0.getAccessTokenSilently({
              cacheMode: 'off',
              authorizationParams: {
                scope: 'offline_access',
                eeNamespace: this.configService.env.eeNamespace
              }
            })
          )
        );
      }),
      tap((token) => {
        if (token) {
          this.auth0TokenBehaviorSubject$.next(token);
          const decoded: ITokenInfo = jwt_decode(token);
          this.segmentService.identifyUser(decoded.meta.authsvc_account_id);
        }
      })
    );
  }

  setAuth0Success(): void {
    this.authSuccess$.next(true);
  }

  getAuth0Success(): boolean {
    return this.authSuccess$.getValue();
  }

  logout(logoutOptions: LogoutOptions = { logoutParams: { returnTo: window.location.origin } }): Observable<void> {
    return this.auth0.logout(logoutOptions);
  }

  startHeartbeat(heartbeatInterval: number = 14 * 60 * 1000) {
    if (!this.auth0Enabled) return;

    this.stopHeartbeat();

    this.heartbeatSubscription = interval(heartbeatInterval).pipe(
      switchMap(() => this.auth0.getAccessTokenSilently({
        cacheMode: 'off',
        authorizationParams: {
          scope: 'offline_access',
          eeNamespace: this.configService.env.eeNamespace
        }
      })
      )
    ).subscribe({
      next: () => this.logger.info('Token refreshed via heartbeat'),
      error: (err) => this.logger.error('Heartbeat token refresh failed:', err)
    });
  }

  stopHeartbeat(): void {
    if (this.heartbeatSubscription) {
      this.heartbeatSubscription.unsubscribe();
      this.heartbeatSubscription = null;
    }
  }

  setFirstLoginFromAuth0Signup(): void {
    this.isFirstLoginFromAuth0Signup.next(true);
  }

  completeFirstTimeAuth0Signup(): void {
    this.isFirstLoginFromAuth0Signup.next(false);
  }
}
